import { DisplayData, TenantOrderLine } from '~/lib/model';
import { Big } from 'big.js';
import TwtJob from '~/tenants/twt/model/TwtJob';
import { schema_latest_version, ZodVersionedMetadata } from '~/lib/zod';
import TwtAerialConfig, { TwtAerialType } from '~/tenants/twt/performable/aerial/TwtAerialConfig';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { FileData } from '~/lib/schema/order';
import { DeliverableType } from '~common/model/Deliverable';
import { TwtOrderType } from '~/tenants/twt/model/enums';
import { twt_mini_aerial_price, twt_version, TwtVersion } from '~/tenants/twt/data';

export const TWT_AERIAL_PIN_PHOTO = 10;
export const TWT_AERIAL_BOUNDARY_PHOTO = 10;
export const TWT_AERIAL_PIN_VIDEO = 15;
export const TWT_AERIAL_BOUNDARY_VIDEO = 15;
export const TWT_AERIAL_VIDEO_REVENUE = 285;
export const TWT_AERIAL_VIDEO_EXPENSE = 225;
export const TWT_AERIAL_PHOTO_VIDEO_REVENUE = 325;
export const TWT_AERIAL_PHOTO_VIDEO_EXPENSE = 260;
export const TWT_AERIAL_PHOTO_REVENUE = 215;
export const TWT_NUCLEUS_AERIAL_VIDEO_REVENUE = 245;
export const TWT_NUCLEUS_AERIAL_VIDEO_EXPENSE = 125;

const v4ExpenseMapping: Record<string, string> = {
  '160.00': '155.00',
  '225.00': '215.00',
};

const v4RevenueMapping: Record<string, string> = {
  '215.00': '200.00',
  '285.00': '275.00',
};

export class TwtAerialJob extends TwtJob<typeof TwtAerialConfig> {
  get performable() {
    return TwtAerialConfig;
  }

  get deliverable(): DeliverableType | null {
    if (this.metadata.type === TwtAerialType.VIDEO && this.order.jobs.some((j) => j.performable_id === 'video')) {
      // aerial and interior video are uploaded as a single video
      return null;
    }

    return DeliverableType.AERIAL;
  }

  get files(): Pick<FileData, 'name' | 'bytes' | 'url' | 'mime'>[] {
    if (!this.metadata.survey) {
      return [];
    }

    return [this.metadata.survey];
  }

  defaultValue(): ZodVersionedMetadata<(typeof TwtAerialConfig)['schema']> {
    // TODO: type fix?
    return {
      version: schema_latest_version(TwtAerialConfig.schema),
      type: TwtAerialType.PHOTO_MINI,
      pin_text: [],
    } as any;
  }

  onsite(): number {
    if(this.order.metadata.transfer){
      return 0;
    }

    const onsite = 30;

    if (this.provider) {
      if (this.metadata.type === TwtAerialType.PHOTO_MINI) {
        return this.provider.config.aerial_onsite_mini ?? onsite;
      }

      if (this.metadata.type === TwtAerialType.PHOTO_FULL) {
        return this.provider.config.aerial_onsite_full ?? onsite;
      }

      if (this.metadata.type === TwtAerialType.VIDEO) {
        return this.provider.config.aerial_onsite_video ?? onsite;
      }

      if (this.metadata.type === TwtAerialType.PHOTO_VIDEO) {
        return this.provider.config.aerial_onsite_photo_video ?? onsite;
      }
    }

    return onsite;
  }

  get eligible(): boolean {
    return (
      this.order.metadata.type !== TwtOrderType.SHORT_TERM_RENTAL &&
      this.order.metadata.type !== TwtOrderType.LONG_TERM_RENTAL
    );
  }

  info(): DisplayData[] {
    const info = super.info();

    if (this.metadata.pin_text && this.metadata.pin_text.length > 0) {
      const photos = this.metadata.pin_text.filter((l) => l.type != 'video');
      const videos = this.metadata.pin_text.filter((l) => l.type != 'photo');

      if (photos.length > 0) {
        info.push({
          name: 'Photo Pin/Text',
          value: photos.map((l) => l.location),
          provider: true,
        });

        info.push({
          name: 'Photo Pin/Text',
          value: photos.length.toString(),
          schedule: true,
        });
      }

      if (videos.length > 0) {
        info.push({
          name: 'Video Pin/Text',
          value: videos.map((l) => l.location),
          provider: true,
        });

        info.push({
          name: 'Video Pin/Text',
          value: videos.length.toString(),
          schedule: true,
        });
      }
    }

    if (this.metadata.boundaries_photo && this.metadata.boundaries_photo > 0) {
      info.push({
        name: 'Photo Boundary Lines',
        value: this.metadata.boundaries_photo.toString(),
        provider: true,
        schedule: true,
      });
    }

    if (this.metadata.boundaries_video && this.metadata.boundaries_video > 0) {
      info.push({
        name: 'Video Boundary Lines',
        value: this.metadata.boundaries_video.toString(),
        provider: true,
        schedule: true,
      });
    }

    if (this.metadata.instructions) {
      info.push({
        name: 'Special Instructions',
        value: this.metadata.instructions,
        provider: true,
        schedule: true,
      });
    }

    return info;
  }

  get short() {
    if (this.metadata.type === TwtAerialType.PHOTO_MINI) {
      return 'MINI';
    }

    if (this.metadata.type === TwtAerialType.PHOTO_FULL) {
      return 'FULL';
    }

    if (this.metadata.type === TwtAerialType.VIDEO) {
      return 'VIDEO';
    }

    return 'P + V';
  }

  get name() {
    if (this.metadata.type === TwtAerialType.PHOTO_MINI) {
      return 'Mini Aerial';
    }

    if (this.metadata.type === TwtAerialType.PHOTO_FULL) {
      return 'Full Aerial';
    }

    if (this.metadata.type === TwtAerialType.VIDEO) {
      return 'Aerial Video';
    }

    return 'Aerial Photos + Video';
  }

  get media() {
    if (this.deliverable) {
      return PersonaType.PROVIDER;
    }

    return null;
  }

  get configurable() {
    return true;
  }

  get minimumPhotos() {
    if (this.metadata.type === TwtAerialType.PHOTO_MINI) {
      return 5;
    }

    if (this.metadata.type === TwtAerialType.PHOTO_FULL) {
      return 15;
    }

    if (this.metadata.type === TwtAerialType.VIDEO) {
      return 0;
    }

    return 10;
  }

  private get nucleusVideoOnly() {
    return this.metadata.type === TwtAerialType.VIDEO && this.orderHasNucleus && twt_version(this.order.created, TwtVersion.V22);
  }

  expenseLines(): TenantOrderLine[] {
    const lines: TenantOrderLine[] = [];

    if (this.metadata.pin_text && this.metadata.pin_text.length > 0) {
      const photos = this.metadata.pin_text.filter((l) => l.type != 'video');
      const videos = this.metadata.pin_text.filter((l) => l.type != 'photo');

      if (photos.length > 0) {
        lines.push({
          description: `Photo Pin/Text Combo (${photos.length})`,
          amount: new Big(TWT_AERIAL_PIN_PHOTO).minus(1).times(this.metadata.pin_text.length),
          id: 'pin_text_photo',
        });
      }

      if (videos.length > 0) {
        lines.push({
          description: `Video Pin/Text Combo (${videos.length})`,
          amount: new Big(TWT_AERIAL_PIN_VIDEO).minus(1).times(this.metadata.pin_text.length),
          id: 'pin_text_video',
        });
      }
    }

    if (this.metadata.boundaries_photo && this.metadata.boundaries_photo > 0) {
      lines.push({
        amount: new Big(TWT_AERIAL_BOUNDARY_PHOTO).minus(1).times(this.metadata.boundaries_photo),
        description: `Photo Boundary Lines (${this.metadata.boundaries_photo})`,
        id: 'boundary_photo',
      });
    }

    if (this.metadata.boundaries_video && this.metadata.boundaries_video > 0) {
      lines.push({
        amount: new Big(TWT_AERIAL_BOUNDARY_VIDEO).minus(1).times(this.metadata.boundaries_video),
        description: `Video Boundary Lines (${this.metadata.boundaries_video})`,
        id: 'boundary_video',
      });
    }

    if (!twt_version(this.order.created, TwtVersion.V19) || !this.orderHasVideo) {
      if (this.metadata.type === TwtAerialType.PHOTO_VIDEO) {
        let amount = new Big(260);

        if (this.order.metadata.type === TwtOrderType.COMMERCIAL) {
          amount = twt_version(this.order.created, TwtVersion.V9) ? new Big('406.25') : new Big(390);
        }

        lines.push({
          amount,
          description: lines.length === 0 ? 'Aerial Video + Photos' : 'Video + Photos',
          id: 'video_photos',
        });
      } else if (this.metadata.type !== TwtAerialType.VIDEO) {
        let amount = new Big(160);

        if (this.order.metadata.type === TwtOrderType.COMMERCIAL) {
          amount = new Big(twt_version(this.order.created, TwtVersion.V9) ? '169.00' : '162.50');
        } else if (this.metadata.type === TwtAerialType.PHOTO_MINI) {
          amount = new Big(twt_version(this.order.created, TwtVersion.V21) ? 105 : 100);
        }

        if (!twt_version(this.order.created, TwtVersion.V4) && v4ExpenseMapping[amount.toFixed(2)]) {
          amount = new Big(v4ExpenseMapping[amount.toFixed(2)]);
        }

        lines.push({
          amount,
          description: lines.length === 0 ? 'Aerial Photos' : 'Photos',
          id: 'photo',
        });
      } else {
        // video
        let amount = new Big(this.nucleusVideoOnly ? TWT_NUCLEUS_AERIAL_VIDEO_EXPENSE : 225);

        if (this.order.metadata.type === TwtOrderType.COMMERCIAL) {
          amount = new Big(twt_version(this.order.created, TwtVersion.V9) ? 273 : 260);
        }

        if (!twt_version(this.order.created, TwtVersion.V4) && v4ExpenseMapping[amount.toFixed(2)]) {
          amount = new Big(v4ExpenseMapping[amount.toFixed(2)]);
        }

        lines.push({
          amount,
          description: lines.length === 0 ? 'Aerial Video' : 'Video',
          id: 'video',
        });
      }
    }else if(this.orderHasVideo && twt_version(this.order.created, TwtVersion.V21) && this.minimumPhotos && this.order.metadata.type !== TwtOrderType.COMMERCIAL) {
      lines.push({
        amount: new Big(20),
        description: 'Cinematic Aerial Photos',
        id: 'photo',
      });
    }else if(this.metadata.type === TwtAerialType.VIDEO && this.order.metadata.type == TwtOrderType.COMMERCIAL){
      lines.push({
        amount: new Big(273),
        description: this.name,
        id: 'video',
        discountable: true,
        taxable: true,
      });
    }

    return lines;
  }

  revenueLines(): TenantOrderLine[] {
    const lines: TenantOrderLine[] = [];
    const commercial = this.order.metadata.type === TwtOrderType.COMMERCIAL ? 5 : 0;

    if (this.metadata.pin_text && this.metadata.pin_text.length > 0) {
      const photos = this.metadata.pin_text.filter((l) => l.type != 'video');
      const videos = this.metadata.pin_text.filter((l) => l.type != 'photo');

      if (photos.length > 0) {
        lines.push({
          description: `Photo Pin/Text Combo (${photos.length})`,
          amount: new Big(TWT_AERIAL_PIN_PHOTO).plus(commercial).times(this.metadata.pin_text.length),
          id: 'pin_text_photo',
          taxable: true,
          discountable: true,
        });
      }

      if (videos.length > 0) {
        lines.push({
          description: `Video Pin/Text Combo (${videos.length})`,
          amount: new Big(TWT_AERIAL_PIN_VIDEO).plus(commercial).times(this.metadata.pin_text.length),
          id: 'pin_text_video',
          taxable: true,
          discountable: true,
        });
      }
    }

    if (this.metadata.boundaries_photo && this.metadata.boundaries_photo > 0) {
      lines.push({
        amount: new Big(TWT_AERIAL_BOUNDARY_PHOTO).plus(commercial).times(this.metadata.boundaries_photo),
        description: `Photo Boundary Lines (${this.metadata.boundaries_photo})`,
        id: 'boundary_photo',
        taxable: true,
        discountable: true,
      });
    }

    if (this.metadata.boundaries_video && this.metadata.boundaries_video > 0) {
      lines.push({
        amount: new Big(TWT_AERIAL_BOUNDARY_VIDEO).plus(commercial).times(this.metadata.boundaries_video),
        description: `Video Boundary Lines (${this.metadata.boundaries_video})`,
        id: 'boundary_video',
        taxable: true,
        discountable: true,
      });
    }

    if (!twt_version(this.order.created, TwtVersion.V19) || !this.order.jobs.some(j => j.performable_id === 'video')) {
      if (this.metadata.type === TwtAerialType.PHOTO_VIDEO) {
        let amount = new Big(TWT_AERIAL_PHOTO_VIDEO_REVENUE);

        if (this.order.metadata.type === TwtOrderType.COMMERCIAL) {
          amount = new Big(twt_version(this.order.created, TwtVersion.V9) ? 625 : 600);
        }

        if (!twt_version(this.order.created, TwtVersion.V4) && v4RevenueMapping[amount.toFixed(2)]) {
          amount = new Big(v4RevenueMapping[amount.toFixed(2)]);
        }

        lines.push({
          amount,
          description: lines.length === 0 ? this.name : 'Video + Photos',
          id: 'video_photos',
          taxable: true,
          discountable: true,
        });
      } else if (this.metadata.type !== TwtAerialType.VIDEO) {
        // photo
        let amount = new Big(TWT_AERIAL_PHOTO_REVENUE);

        if (this.order.metadata.type === TwtOrderType.COMMERCIAL) {
          amount = new Big(twt_version(this.order.created, TwtVersion.V9) ? 260 : 250);
        } else if (this.metadata.type === TwtAerialType.PHOTO_MINI) {
          // The $110 promotion window lives in data.ts (TWT_MINI_AERIAL_PROMO),
          // shared with Ask Twist Support's pricing answers.
          amount = new Big(twt_mini_aerial_price(this.order.created));
        }

        if (!twt_version(this.order.created, TwtVersion.V4) && v4RevenueMapping[amount.toFixed(2)]) {
          amount = new Big(v4RevenueMapping[amount.toFixed(2)]);
        }

        lines.push({
          amount,
          description: lines.length === 0 ? this.name : 'Photos',
          id: 'photo',
          discountable: true,
          taxable: true,
        });
      } else {
        // video
        let amount = new Big(this.nucleusVideoOnly ? TWT_NUCLEUS_AERIAL_VIDEO_REVENUE : 285);

        if (this.order.metadata.type === TwtOrderType.COMMERCIAL) {
          amount = new Big(twt_version(this.order.created, TwtVersion.V9) ? 420 : 400);
        }

        if (!twt_version(this.order.created, TwtVersion.V4) && v4RevenueMapping[amount.toFixed(2)]) {
          amount = new Big(v4RevenueMapping[amount.toFixed(2)]);
        }

        lines.push({
          amount,
          description: lines.length === 0 ? this.name : 'Video',
          id: 'video',
          discountable: true,
          taxable: true,
        });
      }
    }else if(this.metadata.type === TwtAerialType.VIDEO && this.order.metadata.type == TwtOrderType.COMMERCIAL){
      lines.push({
        amount: new Big(275),
        description: this.name,
        id: 'video',
        discountable: true,
        taxable: true,
      });
    }

    return lines;
  }
}
